import React, { useState, useEffect } from 'react';
import SoftGrid from '../Shared/SoftGrid/SoftGrid';
import ApiServices from '../Services/ApiServices';
import { toastHelper } from '../../Helpers/toastHelper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faPencilAlt,
    faTrash,
    faSave,
    faLaptopCode,
    faIdBadge,
    faSearch
} from '@fortawesome/free-solid-svg-icons';
import DeleteAlert from '../Shared/DeleteAlert';
import ModalPopUp from '../Shared/ModalPopUp';

const AdminFeatureCatalog = () => {
    const [features, setFeatures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // Dialogs
    const [deleteDialog, setDeleteDialog] = useState({ open: false, feature: null });
    const [modal, setModal] = useState({ open: false, feature: null });
    const [form, setForm] = useState({ name: '', screen_name: '' });

    const fetchFeatures = async () => {
        setLoading(true);
        try {
            const response = await ApiServices.get('Subscription/PlanFeture/getAll');
            if (response && response.data) {
                setFeatures(response.data);
            }
        } catch (error) {
            toastHelper.error('فشل في تحميل قاموس المميزات');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFeatures();
    }, []);

    const handleSave = async () => {
        if (!form.name.trim() || !form.screen_name.trim()) {
            return toastHelper.error('يرجى ملء جميع الحقول المطلوبة');
        }

        setSubmitting(true);
        try {
            let response;
            if (modal.feature) {
                // Update
                response = await ApiServices.put(`Subscription/PlanFeture/update/${modal.feature.id}`, {
                    name: form.name.trim(),
                    screen_name: form.screen_name.trim()
                });
            } else {
                // Create
                response = await ApiServices.post('Subscription/PlanFeture/create', {
                    name: form.name.trim(),
                    screen_name: form.screen_name.trim()
                });
            }

            if (response && response.check) {
                toastHelper.success(modal.feature ? 'تم تحديث الميزة بنجاح' : 'تمت إضافة الميزة للقائمة');
                setModal({ open: false, feature: null });
                fetchFeatures();
            } else {
                toastHelper.error(response.message || 'فشل في العملية');
            }
        } catch (error) {
            toastHelper.error('حدث خطأ أثناء الاتصال بالسيرفر');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        try {
            setLoading(true);
            const response = await ApiServices.delete(`Subscription/PlanFeture/delete/${deleteDialog.feature.id}`);
            if (response && response.check) {
                toastHelper.success('تم حذف الميزة من القاموس بنجاح');
                fetchFeatures();
            }
        } catch (error) {
            toastHelper.error('فشل في الحذف، قد تكون الميزة مرتبطة بخطط نشطة');
        } finally {
            setLoading(false);
            setDeleteDialog({ open: false, feature: null });
        }
    };

    const openModal = (feature = null) => {
        if (feature) {
            setForm({ name: feature.name, screen_name: feature.screen_name || '' });
            setModal({ open: true, feature });
        } else {
            setForm({ name: '', screen_name: '' });
            setModal({ open: true, feature: null });
        }
    };

    const columns = [
        {
            key: 'id',
            label: 'ID',
            width: '80px',
            render: (val) => <span className="id-badge">#{val}</span>
        },
        {
            key: 'name',
            label: 'اسم الميزة',
            sortable: true,
            render: (val) => <span className="feature-name-text">{val}</span>
        },
        {
            key: 'screen_name',
            label: 'الاسم البرمجي (Screen)',
            render: (val) => <code className="screen-code">{val || '-'}</code>
        }
    ];

    const actions = (row) => [
        {
            label: 'تعديل',
            icon: <FontAwesomeIcon icon={faPencilAlt} />,
            onClick: (row) => openModal(row),
            className: 'edit-btn'
        },
        {
            label: 'حذف',
            icon: <FontAwesomeIcon icon={faTrash} />,
            onClick: (row) => setDeleteDialog({ open: true, feature: row }),
            className: 'delete-btn'
        }
    ];

    return (
        <div className="feature-catalog-container">
            <div className="page-header">
                <div className="header-info">
                    <h2>قاموس المميزات</h2>
                    <p>إدارة قائمة المميزات العامة التي يمكن إضافتها لخطط الاشتراك ({features.length})</p>
                </div>
                <button className="add-btn" onClick={() => openModal()}>
                    <FontAwesomeIcon icon={faPlus} />
                    <span>إضافة ميزة للقاموس</span>
                </button>
            </div>

            <SoftGrid
                columns={columns}
                data={features}
                isLoading={loading}
                actions={actions}
                searchPlaceholder="ابحث في المميزات..."
            />

            <ModalPopUp
                isOpen={modal.open}
                onClose={() => setModal({ open: false, feature: null })}
                title={modal.feature ? 'تعديل ميزة في القاموس' : 'إضافة ميزة جديدة للقاموس'}
            >
                <div className="feature-catalog-form">
                    <div className="input-group">
                        <label><FontAwesomeIcon icon={faIdBadge} /> اسم الميزة المعروض</label>
                        <input
                            type="text"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="مثلاً: دعم الذكاء الاصطناعي"
                        />
                    </div>
                    <div className="input-group">
                        <label><FontAwesomeIcon icon={faLaptopCode} /> الاسم البرمجي (Screen Name)</label>
                        <input
                            type="text"
                            value={form.screen_name}
                            onChange={(e) => setForm({ ...form, screen_name: e.target.value })}
                            placeholder="مثلاً: AI_SUPPORT_SCREEN"
                        />
                        <small>يستخدم للتحقق من الصلاحيات برمجياً</small>
                    </div>

                    <button className="submit-btn" onClick={handleSave} disabled={submitting}>
                        <FontAwesomeIcon icon={faSave} spin={submitting} />
                        <span>{submitting ? 'جاري الحفظ...' : 'حفظ الميزة'}</span>
                    </button>
                </div>
            </ModalPopUp>

            <DeleteAlert
                isOpen={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, feature: null })}
                onConfirm={handleDelete}
                itemName={`الميزة ${deleteDialog.feature?.name}`}
                loading={loading}
            />

            <style>{`
                .feature-catalog-container { padding: 20px; direction: rtl; }
                .page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; }
                .header-info h2 { margin: 0; color: #1e293b; font-size: 1.5rem; font-weight: 800; }
                .header-info p { margin: 4px 0 0; color: #64748b; }
                
                .add-btn { background: #6366f1; color: white; border: none; padding: 12px 24px; border-radius: 12px; display: flex; align-items: center; gap: 10px; font-weight: 700; cursor: pointer; transition: all 0.2s; box-shadow: 0 4px 6px -1px rgba(99, 102, 241, 0.4); }
                .add-btn:hover { background: #4f46e5; transform: translateY(-1px); }

                .id-badge { background: #f1f5f9; color: #64748b; padding: 4px 8px; border-radius: 6px; font-weight: 700; font-size: 0.85rem; }
                .feature-name-text { font-weight: 700; color: #334155; }
                .screen-code { background: #f8fafc; color: #6366f1; padding: 4px 8px; border-radius: 6px; font-size: 0.85rem; font-family: monospace; border: 1px solid #e2e8f0; }

                .feature-catalog-form { display: flex; flex-direction: column; gap: 20px; padding: 10px; }
                .feature-catalog-form .input-group label { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; font-weight: 700; color: #475569; }
                .feature-catalog-form input { width: 100%; padding: 12px; border: 2px solid #e2e8f0; border-radius: 10px;   font-family: "Zain", sans-serif; transition: all 0.2s; }
                .feature-catalog-form input:focus { border-color: #6366f1; outline: none; }
                .feature-catalog-form small { color: #94a3b8; font-size: 0.8rem; margin-top: 4px; display: block; }
                
                .submit-btn { background: #6366f1; color: white; border: none; padding: 14px; border-radius: 10px; font-weight: 700; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 10px; margin-top: 10px; transition: all 0.2s; }
                .submit-btn:hover { background: #4f46e5; }
                .submit-btn:disabled { opacity: 0.7; cursor: not-allowed; }
            `}</style>
        </div>
    );
};

export default AdminFeatureCatalog;
